import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { PageShell } from '@/components/common/PageShell';
import { Spinner } from '@/components/common/Spinner';
import { adminAuthApi } from '@/services/adminAuthApi';
import { setUnauthorizedHandler } from '@/services/apiClient';
import type { AdminUser } from '@/types/admin';
import { AuthContext, type AuthStatus, type AuthValue } from './auth-context';

/**
 * Holds the answer to "is anyone signed in", for everything under it.
 *
 * Mounted as a layout route rather than around the whole app, and that is the
 * load-bearing decision: the meeting route sits outside it, so a patient
 * opening a visit link never issues an auth probe, never waits on one, and can
 * never be redirected by one.
 *
 * While the probe is in flight it renders a spinner instead of its outlet.
 * Both the login page and the panel live under that outlet, so neither can
 * paint before the answer arrives — no flash of the login form for someone who
 * is signed in, no flash of the panel for someone who is not.
 */
export function AuthProvider() {
  const [status, setStatus] = useState<AuthStatus>('unknown');
  const [user, setUser] = useState<AdminUser | null>(null);
  const [reason, setReason] = useState<string | null>(null);

  /** When the tab was last hidden, so a long absence triggers a re-check. */
  const hiddenSince = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const { user: account } = await adminAuthApi.me();
      setUser(account);
      setStatus(account ? 'authed' : 'anonymous');
    } catch {
      // A backend that cannot answer is not a signed-in session. The login
      // screen will say what went wrong when they try.
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const expire = useCallback((why: string) => {
    setUser(null);
    setStatus('anonymous');
    setReason(why);
  }, []);

  /**
   * One place recognises a dead session, so three cards polling at once
   * produce one state change and one redirect instead of three.
   */
  useEffect(() => {
    setUnauthorizedHandler(() =>
      expire('Your session ended. Sign in again to continue.'),
    );
    return () => setUnauthorizedHandler(null);
  }, [expire]);

  /**
   * A tab left open overnight has a cookie the server has long forgotten.
   * Without this, coming back to it shows a fully rendered panel whose first
   * click answers 401; with it, they land on the login screen with a reason.
   */
  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        hiddenSince.current = Date.now();
        return;
      }
      const away = hiddenSince.current;
      hiddenSince.current = null;
      if (away !== null && Date.now() - away > 60_000) {
        void load();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [load]);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      user,
      reason,
      async signIn(email, password) {
        const { user: account } = await adminAuthApi.login(email, password);
        setUser(account);
        setStatus('authed');
        setReason(null);
      },
      async signOut() {
        try {
          await adminAuthApi.logout();
        } catch {
          // A sign-out that fails still locks the screen: the one moment
          // someone wants to be signed out is the worst moment to argue.
        }
        setUser(null);
        setStatus('anonymous');
        setReason(null);
      },
      refresh: load,
      expire,
    }),
    [status, user, reason, load, expire],
  );

  return (
    <AuthContext.Provider value={value}>
      {status === 'unknown' ? (
        <PageShell>
          <Spinner />
        </PageShell>
      ) : (
        <Outlet />
      )}
    </AuthContext.Provider>
  );
}
